import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { PROJECT_STATUSES } from "../lib/constants";
import { Field, Options, useBusy } from "./common";
import CustomerPicker from "./CustomerPicker";
import { AutofillButton } from "./ai/AutofillDialog";

export default function ProjectForm({ initial, onClose, onSaved }) {
  const { t, lang } = useLang();
  const { can, user } = useAuth();
  const [f, setF] = useState({
    customer_id: "", name: "", address: "", scope: "", start_date: "", expected_completion: "", completion_date: "",
    crew_ids: [], status: "scheduled", notes: "", instructions: "", materials: "", contract_amount: 0,
    deposit_required: 0, deposit_received: 0, additional_received: 0, warranty_years: 1, warranty_notes: "",
    ...(initial || {}),
  });
  const [users, setUsers] = useState([]);
  const [busy, run] = useBusy();
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active))).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleCrew = (id) =>
    setF({ ...f, crew_ids: f.crew_ids.includes(id) ? f.crew_ids.filter((x) => x !== id) : [...f.crew_ids, id] });
  const save = (e) =>
    run(async () => {
      e.preventDefault();
      if (!f.customer_id) return toast.error(t("Select a customer", "Seleccione un cliente"));
      try {
        const { data } = initial?.id ? await api.put(`/projects/${initial.id}`, f) : await api.post("/projects", f);
        toast.success(t("Project saved", "Proyecto guardado"));
        onSaved?.(data);
        onClose();
      } catch (err) {
        toast.error(errMsg(err));
      }
    });
  const inp = (k, l, type = "text") => (
    <Field label={l}>
      <input className="inp" type={type} step="any" value={f[k] ?? ""} onChange={set(k)} data-testid={`project-form-${k}`} />
    </Field>
  );
  const txt = (k, l) => (
    <Field label={l} ai={{ form: "project", field: k, value: f[k], onInsert: (v) => setF((x) => ({ ...x, [k]: v })), ctx: { customer_id: f.customer_id, project_id: initial?.id } }}>
      <textarea className="inp" value={f[k] || ""} onChange={set(k)} data-testid={`project-form-${k}`} />
    </Field>
  );
  const labels = { name: t("Project name", "Nombre del proyecto"), address: t("Address", "Dirección"), scope: t("Scope of work", "Alcance del trabajo"), instructions: t("Instructions for crew", "Instrucciones para el equipo"), materials: t("Materials", "Materiales"), notes: t("Notes", "Notas"), status: t("Status", "Estado"), start_date: t("Start date", "Fecha de inicio"), expected_completion: t("Expected completion", "Terminación estimada"), warranty_years: t("Warranty (years)", "Garantía (años)"), warranty_notes: t("Warranty notes", "Notas de garantía"), contract_amount: t("Contract amount", "Monto del contrato"), deposit_required: t("Deposit required", "Depósito requerido") };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? t("Edit Project", "Editar proyecto") : t("New Project", "Nuevo proyecto")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <AutofillButton form="project" title={initial?.id ? t("Autofill with AI", "Autollenar con IA") : t("Create with AI", "Crear con IA")} current={f} labels={labels} ctx={{ customer_id: f.customer_id, project_id: initial?.id }} onApply={(v) => setF((x) => ({ ...x, ...v }))} className="w-full h-11" />
          {!initial?.id && (
            <Field label={t("Customer *", "Cliente *")}>
              <CustomerPicker value={f.customer_id} onChange={(id, c) => setF({ ...f, customer_id: id, address: f.address || c?.property_address || "", name: f.name || c?.service_requested || "" })} testid="project-form-customer" />
            </Field>
          )}
          {inp("name", t("Project name", "Nombre del proyecto"))}
          {inp("address", t("Address", "Dirección"))}
          <Field label={t("Status", "Estado")}>
            <select className="inp" value={f.status} onChange={set("status")} data-testid="project-form-status">
              <Options items={PROJECT_STATUSES} lang={lang} />
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            {inp("start_date", t("Start date", "Fecha de inicio"), "date")}
            {inp("expected_completion", t("Expected completion", "Terminación estimada"), "date")}
          </div>
          {inp("completion_date", t("Actual completion date", "Fecha real de terminación"), "date")}
          <div>
            <span className="label">{t("Assigned crew", "Equipo asignado")}</span>
            <div className="flex flex-wrap gap-2">
              {users.map((u) => (
                <button type="button" key={u.id} onClick={() => toggleCrew(u.id)} data-testid={`project-form-crew-${u.id}`}
                  className={`btn-sm btn ${f.crew_ids.includes(u.id) ? "bg-slate-900 text-white" : "bg-white border border-slate-300"}`}>
                  {u.name}
                </button>
              ))}
            </div>
          </div>
          {txt("scope", t("Scope of work", "Alcance del trabajo"))}
          {txt("instructions", t("Instructions for crew", "Instrucciones para el equipo"))}
          {txt("materials", t("Materials", "Materiales"))}
          {txt("notes", t("Notes", "Notas"))}
          {can("view_prices") && (
            <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-3">
              {inp("contract_amount", t("Contract amount", "Monto del contrato"), "number")}
              {inp("deposit_required", t("Deposit required", "Depósito requerido"), "number")}
              {inp("deposit_received", t("Deposit received", "Depósito recibido"), "number")}
              {inp("additional_received", t("Other payments received", "Otros pagos recibidos"), "number")}
              <p className="col-span-2 text-xs text-slate-500">{t("Manual record only. No payments are processed in this app.", "Solo registro manual. Esta aplicación no procesa pagos.")}</p>
            </div>
          )}
          {user?.role === "owner" && inp("warranty_years", t("Warranty (years)", "Garantía (años)"), "number")}
          {txt("warranty_notes", t("Warranty notes", "Notas de garantía"))}
          <button className="btn-primary w-full" disabled={busy} data-testid="project-form-save-btn">
            {t("Save Project", "Guardar proyecto")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
